import { getBaggageEntryByName } from '@makemydeal/dr-platform-shared';
import type { Offer, OfferType } from '@makemydeal/dr-platform-types';
import { ApplicationCodes, LEASE, RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import type {
    PartialPenciledInputsWithAllTypes,
    PenciledInputsState,
    PriorLeaseBalance,
    PriorLeaseBalanceApplyMethod,
    PriorLeaseBalanceMethod
} from '../../../types/commonTypes';
import * as selectors from '../../../selectors';
import { getSpecialPaymentFormula } from '../../../selectors';
import * as types from '../../../types/actionTypes';
import { ASP_PG_REDESIGN_TOGGLE } from '../../../constants/meta';
import { CALC_ENDPOINTS } from '../../../constants/state';
import { isPricedWithLeaseResidualOverride, LEASE_RESIDUAL_OVERRIDE_BAGGAGE_NAME } from '../../../utils/leaseResidualOverrideUtils';
import type { LeaseResidualOverride } from '../../../utils/leaseResidualOverrideUtils';
import { cleanSavedRatesWhenEnabled } from '../../../utils/savedTermRateUtils';
import { getLoadPayloadFeatureToggles } from '../../../utils/loadPayloadFeatureToggles';
import { reducerConfig } from '../../reducerConfig';

const PRIOR_BALANCE_TOGGLE = 'F300850_Add_Prior_Balance';
const PS_METHOD_TO_APPLY_METHOD = {
    Capitalize: 'Capitalized',
    Upfront: 'Upfront',
    AddToPrice: 'Capitalized'
} as const satisfies Record<PriorLeaseBalanceMethod, PriorLeaseBalanceApplyMethod>;

const toApplyMethod = (priorLeaseBalanceMethod: unknown): PriorLeaseBalanceApplyMethod | undefined =>
    typeof priorLeaseBalanceMethod === 'string' &&
    Object.prototype.hasOwnProperty.call(PS_METHOD_TO_APPLY_METHOD, priorLeaseBalanceMethod)
        ? PS_METHOD_TO_APPLY_METHOD[priorLeaseBalanceMethod as PriorLeaseBalanceMethod]
        : undefined;

const getLoadedPriorLeaseBalance = (payload: any): PriorLeaseBalance | undefined => {
    if (!getLoadPayloadFeatureToggles(payload)?.[PRIOR_BALANCE_TOGGLE]) {
        return undefined;
    }

    const payment = payload.offer?.payment;
    const amount = payment?.priorLeaseBalance;

    if (typeof amount !== 'number' || Number.isNaN(amount)) {
        return undefined;
    }

    const applyMethod = toApplyMethod(payment.priorLeaseBalanceMethod);
    const payableTo = payment.priorLeaseBalancePayableTo ?? payment.payableTo;
    const vin = payment.priorLeaseBalanceVin ?? payment.vin;
    const lenderOwed = payment.priorLeaseBalanceLenderOwed ?? payment.lenderOwed;

    return {
        amount,
        ...(applyMethod && { applyMethod }),
        ...(payableTo && { payableTo }),
        ...(vin && { vin }),
        ...(lenderOwed && { lenderOwed })
    };
};

const getLoadedLeaseResidualOverride = (payload: any): LeaseResidualOverride | undefined => {
    if (!getLoadPayloadFeatureToggles(payload)?.[ASP_PG_REDESIGN_TOGGLE]) return undefined;
    if (reducerConfig?.sourceApplication !== ApplicationCodes.MV) return undefined;

    const loadedEndpoint = payload.calculationDetails?.payment?.calculationEngine?.endpoint ?? CALC_ENDPOINTS.programQuotes;
    if (loadedEndpoint !== CALC_ENDPOINTS.programQuotes) return undefined;

    const baggage = Array.isArray(payload.baggage) ? payload.baggage : undefined;
    const content = getBaggageEntryByName<{ content?: Partial<LeaseResidualOverride> }>(
        baggage,
        LEASE_RESIDUAL_OVERRIDE_BAGGAGE_NAME
    )?.value?.content;
    const { months, unit, value } = content ?? {};

    if (typeof months !== 'number' || typeof value !== 'number') return undefined;
    if (unit !== RESIDUAL_DOLLAR && unit !== RESIDUAL_PERCENT) return undefined;

    return { months, unit, value };
};

const withLeaseResidualOverride = (
    overridesByTerm: Record<number, any> | undefined,
    { months, unit, value }: LeaseResidualOverride
): Record<number, any> => ({
    ...overridesByTerm,
    [months]: {
        ...overridesByTerm?.[months],
        residualInfo: {
            ...overridesByTerm?.[months]?.residualInfo,
            baseResidualType: unit,
            ...(unit === RESIDUAL_DOLLAR
                ? { baseResidualAmount: value, programResidual: value }
                : { baseResidualPercent: value, programResidualPercent: value })
        }
    }
});

export const handleInitCases = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    offer: Offer
): PartialPenciledInputsWithAllTypes => {
    switch (action.type) {
        case types.INIT_OFFER: {
            const initInputs = cleanSavedRatesWhenEnabled(
                { ...state, ...action.payload.penciledInputs },
                action.payload.featureToggles
            );
            const deepOffer = { ...offer, penciledInputs: initInputs };
            if (initInputs?.byOfferType?.[offerType]) {
                const totalIncentives: number = selectors.findAllIncentiveTotalFromOffer(deepOffer, offerType);
                initInputs.byOfferType[offerType].totalIncentives = totalIncentives;
            }

            return initInputs;
        }

        case types.INIT_SUCCESS:
        case types.OFFER_SAVE_SUCCESS: {
            const copiedState = structuredClone(state);
            const newState = {
                ...copiedState,
                savedInputs: {
                    ...copiedState.savedInputs,
                    offerType,
                    specialPaymentFormula: getSpecialPaymentFormula(offer)
                }
            };

            if (action.type === types.INIT_SUCCESS) {
                const calculationDetailsPayment = action.payload?.calculationDetails?.payment;

                if (calculationDetailsPayment) {
                    newState.dealerDiscounts = calculationDetailsPayment.itemizedDiscounts;
                }

                // Start the residual type from the value saved with the deal so it reflects the last saved
                // choice on open. Applied only here at load; afterward the type tracks payment responses.
                const isLeaseResidualTreatmentEnabled = !!getLoadPayloadFeatureToggles(action.payload)
                    ?.enableLeaseResidualTreatment_F286227;
                const residualTypeFromCalculationDetails = selectors.getResidualTypeFromCalculationDetails(
                    action.payload?.calculationDetails
                );
                const offerInputs = newState.byOfferType?.[offerType];
                if (isLeaseResidualTreatmentEnabled && offerType === LEASE && residualTypeFromCalculationDetails && offerInputs) {
                    offerInputs.baseResidualTypeOverride = residualTypeFromCalculationDetails;
                }

                const loadedLeaseResidualOverride = getLoadedLeaseResidualOverride(action.payload);
                const selectedLeaseTerm = offerInputs?.term ? offer?.terms?.byId?.[offerInputs.term] : undefined;
                if (
                    offerType === LEASE &&
                    offerInputs &&
                    loadedLeaseResidualOverride &&
                    selectedLeaseTerm?.months === loadedLeaseResidualOverride.months &&
                    isPricedWithLeaseResidualOverride(selectedLeaseTerm, loadedLeaseResidualOverride)
                ) {
                    offerInputs.overridesByTerm = withLeaseResidualOverride(
                        offerInputs.overridesByTerm,
                        loadedLeaseResidualOverride
                    );
                }

                const loadedPriorLeaseBalance = getLoadedPriorLeaseBalance(action.payload);
                if (loadedPriorLeaseBalance) {
                    newState.priorLeaseBalance = loadedPriorLeaseBalance;
                }
            }
            return newState;
        }

        default: {
            return state;
        }
    }
};
